import { CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import { Team } from 'types';
import { DataTableColumns, VisuallyHidden } from '@tq/ui-components';
import {
  teamEditingRowIdState,
  teamEditingRowTeamNameState,
  teamEditingRowEmailState,
  teamEditingRowBackofficeCodeState,
} from 'store';
import { EditableTableCell } from 'components/EditableTableCell';
import { isEmailValid } from 'tools';
import { useMemo } from 'react';
import { TeamRowEditButtons } from '../components';

export const useTeamColumns = (disableEmail: boolean): DataTableColumns<Team>[] => {
  const { t } = useTranslation('manage_organisation');
  const width = 220;

  const handleNameValidation = (value: string): boolean => {
    return value.length > 0;
  };

  const handleEmailValidation = (value: string): boolean => {
    return isEmailValid(value);
  };

  return useMemo(
    () => [
      {
        accessor: 'teamName',
        disableSortBy: false,
        Header: () => t('team_name', 'Team name'),
        Cell: (props: CellProps<Team, unknown>) => (
          <EditableTableCell
            editingState={teamEditingRowIdState}
            rowIndex={props.row.index}
            rowValue={props.row.original.teamName}
            valueState={teamEditingRowTeamNameState}
            label={t('team_name', 'Team name')}
            placeholder={t('team_name_placeholder', 'Name')}
            errorMessage={t('team_name_input_error', 'Please add a team name to continue')}
            type='text'
            disabled={false}
            validator={handleNameValidation}
            dataTestId='name-edit-testid'
          />
        ),
        width: 300,
      },
      {
        accessor: 'backOfficeCode',
        disableSortBy: true,
        Header: () => t('backoffice_code', 'Back Office code'),
        Cell: (props: CellProps<Team, unknown>) => (
          <EditableTableCell
            editingState={teamEditingRowIdState}
            rowIndex={props.row.index}
            rowValue={props.row.original.backOfficeCode}
            valueState={teamEditingRowBackofficeCodeState}
            label={t('backoffice_code', 'Back Office code')}
            placeholder={t('backoffice_code_placeholder', 'Back Office code')}
            errorMessage={t(
              'lpa_backoffice_code_input_error',
              'Please add a LPA backoffice code to continue'
            )}
            type='text'
            disabled={false}
            validator={handleNameValidation}
            dataTestId='code-edit-testid'
          />
        ),
        width: 300,
      },
      {
        accessor: 'email',
        disableSortBy: true,
        Header: () => t('team_email', 'Team email'),
        Cell: (props: CellProps<Team, unknown>) => (
          <EditableTableCell
            editingState={teamEditingRowIdState}
            rowIndex={props.row.index}
            rowValue={props.row.original.email}
            valueState={teamEditingRowEmailState}
            label={t('team_email', 'Team email')}
            placeholder={t('team_email_placeholder', 'Email address')}
            errorMessage={t('team_email_input_error', 'Please enter a valid email address')}
            type='email'
            disabled={disableEmail}
            validator={handleEmailValidation}
            dataTestId='email-edit-testid'
          />
        ),
        width: 'auto',
      },
      {
        accessor: 'msgDetail',
        Cell: (props: CellProps<Team, unknown>) => (
          <TeamRowEditButtons {...props} width={220} emailRequired={!disableEmail} />
        ),
        Header: () => <VisuallyHidden>{t('teams_header', 'Teams')}</VisuallyHidden>,
        width: width,
        disableSortBy: true,
      },
    ],
    [disableEmail, t]
  );
};
